import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, X, Search, Filter, ChevronRight, AlertCircle, CheckCircle, Clock, Loader } from 'lucide-react';
import api from '../services/api';
import CreateWorkflowModal from '../components/CreateWorkflowModal';
import WorkflowDetailModal from '../components/WorkflowDetailModal';
import ProgressDisplay from '../components/ProgressDisplay';
import { format } from 'date-fns';

interface Workflow {
  id: string;
  customer_id: string;
  status: 'INITIATED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  current_step: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  error_message?: string;
  admin_account: string;
  total_accounts: number;
  processed_accounts: number;
  total_work_units: number;
  completed_work_units: number;
  spreadsheet_url?: string;
}

const Workflows: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const queryClient = useQueryClient();

  const { data: workflows, isLoading } = useQuery({
    queryKey: ['workflows'],
    queryFn: api.getWorkflows,
    refetchInterval: 5000,
    // Prevent re-renders if data hasn't actually changed
    structuralSharing: true,
    // Only refetch if data is stale
    refetchIntervalInBackground: false,
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.retryWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  // Helper to get progress label based on current step
  const getProgressLabel = (currentStep: string): string => {
    if (!currentStep) return 'items';
    const step = currentStep.toUpperCase();
    // Domain-related phases
    if (step.includes('DOMAIN') || step.includes('DKIM') || step.includes('CLOUDFLARE')) {
      return 'domains';
    }
    // All other phases use accounts (users, IMAP, warmup)
    return 'accounts';
  };

  const filteredWorkflows = workflows?.filter((w: Workflow) => {
    const matchesSearch = w.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          w.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || w.status === statusFilter;
    return matchesSearch && matchesStatus;
  })?.sort((a: Workflow, b: Workflow) => {
    // Sort by created_at date, newest first
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'FAILED':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'PROCESSING':
        return <Loader className="w-5 h-5 text-orange-400 animate-spin" />;
      case 'CANCELLED':
        return <X className="w-5 h-5 text-gray-400" />;
      default:
        return <Clock className="w-5 h-5 text-blue-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'FAILED':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'PROCESSING':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'CANCELLED':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold premium-gradient">Workflow Management</h1>
          <p className="text-gray-400 mt-1">Orchestrate and monitor automation workflows</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Workflow
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="stat-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Total</div>
          <div className="text-2xl font-bold text-white">{workflows?.length || 0}</div>
        </div>
        <div className="stat-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-500 mb-2">Initiated</div>
          <div className="text-2xl font-bold text-white">
            {workflows?.filter((w: Workflow) => w.status === 'INITIATED').length || 0}
          </div>
        </div>
        <div className="stat-card stat-card-orange">
          <div className="text-xs font-semibold uppercase tracking-wider text-orange-500 mb-2">Processing</div>
          <div className="text-2xl font-bold text-white">
            {workflows?.filter((w: Workflow) => w.status === 'PROCESSING').length || 0}
          </div>
        </div>
        <div className="stat-card stat-card-green">
          <div className="text-xs font-semibold uppercase tracking-wider text-green-500 mb-2">Completed</div>
          <div className="text-2xl font-bold text-white">
            {workflows?.filter((w: Workflow) => w.status === 'COMPLETED').length || 0}
          </div>
        </div>
        <div className="stat-card">
          <div className="text-xs font-semibold uppercase tracking-wider text-red-500 mb-2">Failed</div>
          <div className="text-2xl font-bold text-white">
            {workflows?.filter((w: Workflow) => w.status === 'FAILED').length || 0}
          </div>
        </div>
      </div>

      {/* Real-time Progress Display */}
      <ProgressDisplay />

      {/* Filters */}
      <div className="dashboard-card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by ID or Customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-500 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-gray-900/50 border border-gray-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500/50"
            >
              <option value="all">All Status</option>
              <option value="INITIATED">Initiated</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Workflows Table */}
      <div className="dashboard-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="px-6 py-3 text-left">Status</th>
                <th className="px-6 py-3 text-left">Workflow ID</th>
                <th className="px-6 py-3 text-left">Customer</th>
                <th className="px-6 py-3 text-left">Current Step</th>
                <th className="px-6 py-3 text-left">Progress</th>
                <th className="px-6 py-3 text-left">Created</th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <Loader className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
                    <p className="text-gray-400 mt-2">Loading workflows...</p>
                  </td>
                </tr>
              ) : filteredWorkflows?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-400">
                    No workflows found
                  </td>
                </tr>
              ) : (
                filteredWorkflows?.map((workflow: Workflow) => (
                  <tr key={workflow.id} className="table-row">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(workflow.status)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(workflow.status)}`}>
                          {workflow.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-mono text-sm text-gray-300">{workflow.id.substring(0, 8)}...</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white font-medium">{workflow.customer_id}</div>
                      {workflow.admin_account && (
                        <div className="text-xs text-gray-500">{workflow.admin_account}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{workflow.current_step || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-orange-500 to-yellow-500 transition-all duration-300"
                            style={{
                              width: `${workflow.total_accounts > 0 
                                ? (workflow.processed_accounts / workflow.total_accounts) * 100 
                                : 0}%`
                            }}
                          />
                        </div>
                        <span className="text-xs text-gray-400">
                          {workflow.processed_accounts}/{workflow.total_accounts} {getProgressLabel(workflow.current_step)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-400">
                        {format(new Date(workflow.created_at), 'MMM dd, HH:mm')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {workflow.status === 'FAILED' && (
                          <button
                            onClick={() => retryMutation.mutate(workflow.id)}
                            className="p-1.5 text-green-400 hover:bg-green-500/20 rounded transition-colors"
                            title="Retry"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        {(workflow.status === 'PROCESSING' || workflow.status === 'INITIATED') && (
                          <button
                            onClick={() => cancelMutation.mutate(workflow.id)}
                            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedWorkflow(workflow.id)}
                          className="p-1.5 text-gray-400 hover:text-orange-400 transition-colors"
                          title="View Details"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {isCreateModalOpen && (
        <CreateWorkflowModal onClose={() => setIsCreateModalOpen(false)} />
      )}
      {selectedWorkflow && (
        <WorkflowDetailModal 
          workflowId={selectedWorkflow} 
          onClose={() => setSelectedWorkflow(null)} 
        />
      )}
    </div>
  );
};

export default Workflows;